// WRAITH kit.
//   Ghost Drive (passive): +20% damage while airborne (glide / extra jumps and
//     dashes come from the movement profile and base stats).
//   Phase Echo (signature): 0.9 s phase (i-frames, translucent) and an echo left at
//     the activation point that replays every shot for 4 s at 50%.
//     GHOST_PROTOCOL: +2 s and 75% power. SIGNATURE_EVOLVED: duration ×1.5.
//   Eclipse (ultimate): 5 s of distorted time — every enemy is slowed, enemy
//     projectiles crawl at 30%, and four orbiting echoes mirror each shot at 40%.

import Phaser from 'phaser';
import { DEPTH } from '../../config/constants';
import { COLORS } from '../../config/palette';
import type { EnemyApi, ProjectileHandle } from '../../core/contracts';
import type { DamageInfo } from '../../core/types';
import { captureVolley, Echo } from './echo';
import { needsSlowScale, volleyTarget } from './math';
import { ATEX, sfx, targetableList, type Kit, type KitHost, type TimerHandle } from './shared';

export const WRAITH_TUNING = {
  airborneBonus: 1.2,
  phaseMs: 900,
  phaseAlpha: 0.45,
  echoDuration: 4,
  echoMult: 0.5,
  ghostProtocolBonus: 2,
  ghostProtocolMult: 0.75,
  evolvedDurationMult: 1.5,
  /** Delay between the player's shot and the echo's replay (reads as an echo, not a clone). */
  replayDelay: 0.06,
  eclipseDuration: 5,
  eclipseTick: 0.25,
  eclipseSlow: 0.7,
  eclipseProjectileSpeed: 0.3,
  eclipseEchoes: 4,
  eclipseEchoMult: 0.4,
  eclipseOrbitRadius: 120,
  eclipseOrbitSpeed: 1.7,
} as const;

interface EclipseState {
  until: number;
  slowTick: TimerHandle;
  echoes: Echo[];
  /** Enemy projectiles we slowed → the speed we left them at. */
  slowed: Map<ProjectileHandle, number>;
  tint: Phaser.GameObjects.Rectangle;
  vignette: Phaser.GameObjects.Image;
  age: number;
}

export function createWraithKit(h: KitHost): Kit {
  return new WraithKit(h);
}

class WraithKit implements Kit {
  private phaseUntil = -1;
  private echoes: Echo[] = [];
  private eclipse: EclipseState | null = null;

  constructor(private readonly h: KitHost) {}

  get signatureActive(): boolean {
    return this.phasing || this.echoes.some((e) => e.alive);
  }

  get ultimateActive(): boolean {
    return this.eclipse !== null;
  }

  private get phasing(): boolean {
    return this.h.timers.now < this.phaseUntil;
  }

  start(): void {
    const { ctx } = this.h;
    // Echoes replay every primary volley (never their own replays).
    this.h.on('weapon:fired', (ev) => {
      if (ev.secondary) return;
      const shooters = [...this.echoes, ...(this.eclipse?.echoes ?? [])].filter((e) => e.alive);
      if (shooters.length === 0) return;
      const volley = captureVolley(ev, volleyTarget(ev.x, ev.y, ev.angle, ctx.input.pointerWorld));
      shooters.forEach((echo, i) => {
        // Orbiting echoes ripple one after another.
        this.h.timers.after(WRAITH_TUNING.replayDelay + i * 0.035, () => echo.replay(volley));
      });
    });
  }

  canSignature(): boolean {
    return true;
  }

  outgoing(_target: EnemyApi | null, _info: DamageInfo): number {
    const p = this.h.ctx.player;
    return p.onGround ? 1 : WRAITH_TUNING.airborneBonus;
  }

  incoming(_info: DamageInfo): number {
    return 1;
  }

  // ───────────────────────────── Phase Echo ─────────────────────────────

  signature(): void {
    const { ctx, safe, timers } = this.h;
    const p = ctx.player;
    const T = WRAITH_TUNING;
    const ghost = this.h.flag('GHOST_PROTOCOL');
    const evolved = this.h.flag('SIGNATURE_EVOLVED');

    safe.run('player.setInvulnerable', () => p.setInvulnerable(T.phaseMs));
    this.phaseUntil = timers.now + T.phaseMs / 1000;
    safe.run('view.setAlpha', () => p.view.setAlpha(T.phaseAlpha));

    const duration = (T.echoDuration + (ghost ? T.ghostProtocolBonus : 0)) * (evolved ? T.evolvedDurationMult : 1);
    const echo = new Echo(this.h, {
      x: p.x,
      feetY: p.feetY,
      facing: p.facing,
      duration,
      damageMult: ghost ? T.ghostProtocolMult : T.echoMult,
      alpha: 0.55,
    });
    this.echoes.push(echo);

    // Phase-out burst.
    sfx(ctx, safe, 'phase_echo');
    safe.run('fx.afterimage', () => ctx.fx.afterimage(p.view.root, { color: COLORS.wraith, alpha: 0.7 }));
    safe.run('fx.ring', () => ctx.fx.ring(p.x, p.y, 90, { color: COLORS.wraith, width: 3, fromRadius: 20 }));
    safe.run('fx.sparks', () => ctx.fx.sparks(p.x, p.y, -Math.PI / 2, { count: 12, color: COLORS.wraithGlow, speed: 320, spread: Math.PI * 2 }));
  }

  // ───────────────────────────── Eclipse ─────────────────────────────

  ultimate(): void {
    const { ctx, safe, timers } = this.h;
    const T = WRAITH_TUNING;
    const p = ctx.player;
    const scene = ctx.scene;
    const cam = scene.cameras.main;

    const echoes: Echo[] = [];
    for (let i = 0; i < T.eclipseEchoes; i++) {
      echoes.push(
        new Echo(this.h, {
          x: p.x,
          feetY: p.feetY,
          facing: p.facing,
          duration: T.eclipseDuration,
          damageMult: T.eclipseEchoMult,
          scale: 0.72,
          alpha: 0.42,
          orbit: { phase: (i / T.eclipseEchoes) * Math.PI * 2, radius: T.eclipseOrbitRadius, speed: T.eclipseOrbitSpeed },
        }),
      );
    }

    // Screen treatment: violet vignette + faint tint, below telegraphs so threats stay readable.
    const tint = this.h.track(scene.add.rectangle(cam.width / 2, cam.height / 2, cam.width, cam.height, COLORS.wraith, 1));
    tint.setScrollFactor(0).setDepth(DEPTH.FX).setAlpha(0).setBlendMode(Phaser.BlendModes.ADD);
    const vignette = this.h.track(scene.add.image(cam.width / 2, cam.height / 2, ATEX.vignette));
    vignette.setScrollFactor(0).setDepth(DEPTH.FX).setDisplaySize(cam.width, cam.height).setTint(0x2a0d55).setAlpha(0);

    this.eclipse = {
      until: timers.now + T.eclipseDuration,
      slowTick: timers.every(T.eclipseTick, Math.ceil(T.eclipseDuration / T.eclipseTick), () => this.slowEnemies()),
      echoes,
      slowed: new Map(),
      tint,
      vignette,
      age: 0,
    };

    sfx(ctx, safe, 'eclipse');
    safe.run('fx.flash', () => ctx.fx.flash(COLORS.wraith, 0.25, 220));
    safe.run('fx.ring', () => ctx.fx.ring(p.x, p.y, 420, { color: COLORS.wraith, width: 6, fromRadius: 30 }));
    safe.run('fx.shake', () => ctx.fx.shake(0.006, 260));
    this.slowProjectiles();
  }

  /** Slow every enemy each tick so newly spawned ones are caught too. */
  private slowEnemies(): void {
    const { ctx, safe } = this.h;
    const slow = { id: 'slow' as const, potency: WRAITH_TUNING.eclipseSlow, duration: WRAITH_TUNING.eclipseTick + 0.2 };
    for (const e of targetableList(ctx, safe)) safe.run('status.apply', () => ctx.status.apply(e, slow));
  }

  /** Keep every enemy projectile at 30% speed (new shots and re-used pool handles included). */
  private slowProjectiles(): void {
    const ec = this.eclipse;
    if (!ec) return;
    const { ctx, safe } = this.h;
    const f = WRAITH_TUNING.eclipseProjectileSpeed;
    const seen = new Set<ProjectileHandle>();
    safe.run('projectiles.forEach', () =>
      ctx.projectiles.forEach('enemy', (pr) => {
        if (!pr.alive) return;
        seen.add(pr);
        const speed = Math.hypot(pr.vx, pr.vy);
        if (needsSlowScale(ec.slowed.get(pr), speed)) {
          pr.vx *= f;
          pr.vy *= f;
          ec.slowed.set(pr, speed * f);
        } else {
          ec.slowed.set(pr, speed);
        }
      }),
    );
    for (const pr of ec.slowed.keys()) if (!seen.has(pr)) ec.slowed.delete(pr);
  }

  private endEclipse(): void {
    const ec = this.eclipse;
    if (!ec) return;
    this.eclipse = null;
    ec.slowTick.cancel();
    const f = WRAITH_TUNING.eclipseProjectileSpeed;
    // Time resumes: surviving enemy shots return to full speed.
    for (const [pr] of ec.slowed) {
      if (!pr.alive) continue;
      pr.vx /= f;
      pr.vy /= f;
    }
    ec.slowed.clear();
    for (const e of ec.echoes) e.dismiss();
    this.echoes.push(...ec.echoes); // let them finish fading out in update()
    const scene = this.h.ctx.scene;
    for (const obj of [ec.tint, ec.vignette]) {
      if (!obj.scene) continue;
      scene.tweens.add({
        targets: obj,
        alpha: 0,
        duration: 350,
        onComplete: () => {
          this.h.untrack(obj);
          obj.destroy();
        },
      });
    }
    const p = this.h.ctx.player;
    this.h.safe.run('fx.ring', () => this.h.ctx.fx.ring(p.x, p.y, 200, { color: COLORS.wraithGlow, width: 3, fromRadius: 420 }));
  }

  // ───────────────────────────── frame ─────────────────────────────

  update(dt: number): void {
    const { ctx, safe, timers } = this.h;
    const p = ctx.player;

    // Phase: keep the mech translucent (the player view may reset alpha itself) and restore after.
    if (this.phaseUntil > 0) {
      if (this.phasing) {
        const flicker = 0.06 * Math.sin(timers.now * 40);
        safe.run('view.setAlpha', () => p.view.setAlpha(WRAITH_TUNING.phaseAlpha + flicker));
      } else {
        this.phaseUntil = -1;
        safe.run('view.setAlpha', () => p.view.setAlpha(1));
        safe.run('fx.ring', () => ctx.fx.ring(p.x, p.y, 60, { color: COLORS.wraithGlow, width: 2, fromRadius: 90 }));
      }
    }

    // Dismissed echoes keep updating until their fade-out has destroyed them.
    for (const e of this.echoes) e.update(dt);
    this.echoes = this.echoes.filter((e) => !e.gone);

    const ec = this.eclipse;
    if (ec) {
      if (timers.now >= ec.until) {
        this.endEclipse();
        return;
      }
      ec.age += dt;
      for (const e of ec.echoes) e.update(dt);
      this.slowProjectiles();
      const fadeIn = Math.min(1, ec.age / 0.3);
      const fadeOut = Math.min(1, (ec.until - timers.now) / 0.3);
      const k = Math.min(fadeIn, fadeOut);
      const pulse = 0.5 + 0.5 * Math.sin(ec.age * 5.5);
      if (ec.tint.scene) ec.tint.setAlpha((0.035 + 0.03 * pulse) * k);
      if (ec.vignette.scene) ec.vignette.setAlpha((0.55 + 0.2 * pulse) * k);
    }
  }

  interrupt(): void {
    this.endEclipse();
    if (this.phaseUntil > 0) {
      this.phaseUntil = -1;
      this.h.safe.run('view.setAlpha', () => this.h.ctx.player.view.setAlpha(1));
    }
    for (const e of this.echoes) e.dismiss();
  }

  destroy(): void {
    this.interrupt();
    for (const e of this.echoes) e.destroy();
    this.echoes = [];
  }
}
